/** Roots originate in a local authenticator ceremony, never a hub credential-list response. */

import type {
  PublicKeyCredentialCreationOptionsJSON,
  RegistrationResponseJSON,
} from '@simplewebauthn/browser'
import {
  type ApprovalFailureReason,
  COSE_ALG,
  coseToJwk,
  decodeCbor,
  decodeCborPrefix,
  fromBase64Url,
  fromUtf8,
  type PinnedCredential,
  pinnedCredentialFromCose,
  sha256,
  TrustStore,
  type TrustStoreSnapshot,
  toBase64Url,
  utf8,
  verifyPasskeyApproval,
  type WebAuthnAssertion,
} from '@viko/crypto'
import type { UnlockCredential } from '@viko/protocol'
import { idb } from './idb.js'

const key = (accountId: string) => `content-trust/v1/${accountId}`
export async function loadContentTrust(accountId: string) {
  const saved = await idb.get<TrustStoreSnapshot>(key(accountId))
  if (!saved)
    throw new Error('This browser needs a locally trusted passkey or approved key transfer.')
  return TrustStore.fromJSON(saved)
}
export async function saveContentTrust(accountId: string, trust: TrustStore) {
  await idb.set(key(accountId), trust.toJSON())
}
/** Call ONLY with the direct result of navigator.credentials.create, before sending it anywhere. */
export async function pinLocalRegistration(
  options: PublicKeyCredentialCreationOptionsJSON,
  response: RegistrationResponseJSON,
  origin: string,
) {
  const accountId = fromUtf8(fromBase64Url(options.user.id))
  if (!accountId || accountId.length > 256) throw new Error('invalid registration account')
  const rpId = options.rp.id ?? new URL(origin).hostname
  const client = JSON.parse(fromUtf8(fromBase64Url(response.response.clientDataJSON))) as {
    type?: string
    origin?: string
    challenge?: string
    crossOrigin?: boolean
  }
  if (
    client.type !== 'webauthn.create' ||
    client.origin !== origin ||
    client.challenge !== options.challenge ||
    client.crossOrigin === true
  )
    throw new Error('registration context mismatch')
  const attestation = decodeCbor(fromBase64Url(response.response.attestationObject))
  const authData = attestation instanceof Map ? attestation.get('authData') : undefined
  if (!(authData instanceof Uint8Array) || authData.length < 55 || (authData[32]! & 0x45) !== 0x45)
    throw new Error('verified registration required')
  if (toBase64Url(authData.slice(0, 32)) !== toBase64Url(await sha256(utf8(rpId))))
    throw new Error('registration RP mismatch')
  const length = new DataView(authData.buffer, authData.byteOffset, authData.byteLength).getUint16(
    53,
  )
  if (
    length < 1 ||
    55 + length >= authData.length ||
    toBase64Url(authData.slice(55, 55 + length)) !== response.id ||
    response.id !== response.rawId
  )
    throw new Error('registration credential mismatch')
  const cose = decodeCborPrefix(authData.slice(55 + length))
  if (!(cose.value instanceof Map)) throw new Error('invalid registration key')
  const parsed = coseToJwk(cose.value)
  const pinned = {
    credentialId: response.id,
    publicKey: parsed.jwk,
    alg: parsed.alg,
    addedAt: Date.now(),
  }
  const saved = await idb.get<TrustStoreSnapshot>(key(accountId))
  const trust = saved
    ? await TrustStore.fromJSON(saved)
    : new TrustStore({ rp: { rpId, origins: [origin] }, credentials: [] })
  if (trust.rp.rpId !== rpId || !trust.rp.origins.includes(origin))
    throw new Error('registration trust origin mismatch')
  trust.pinCredential(pinned)
  // Do not replace concurrent directory/trust updates with a stale snapshot.
  await idb.update<TrustStoreSnapshot>(key(accountId), (current) => {
    if (JSON.stringify(current) !== JSON.stringify(saved))
      throw new Error('registration trust changed')
    return trust.toJSON()
  })
  await idb
    .update<Record<string, { source: PinProvenance; at: number }>>(
      provenanceKey(accountId),
      (current) => ({
        ...current,
        [pinned.credentialId]: { source: 'registration', at: pinned.addedAt },
      }),
    )
    .catch(() => {})
  return accountId
}

// ---- pin bootstrap from a locally verified assertion --------------------------------------------

/** Where a pinned passkey came from (kept beside the trust snapshot, never inside it). */
export type PinProvenance = 'registration' | 'escrow' | 'assertion'
const provenanceKey = (accountId: string) => `content-trust-provenance/v1/${accountId}`
export function loadPinProvenance(accountId: string) {
  return idb.get<Record<string, { source: PinProvenance; at: number }>>(provenanceKey(accountId))
}

export type PinBootstrapFailure =
  | 'credential-unavailable'
  | 'not-on-account'
  | 'credential-mismatch'
  | 'wrong-account'
  | 'revoked-credential'
  | 'recovery-hold'
  | 'unsupported-key'
  | ApprovalFailureReason

export type PinBootstrapResult =
  | { ok: true; trust: TrustStore; credential: PinnedCredential }
  | { ok: false; reason: PinBootstrapFailure; detail?: string }

/** Only keys whose signatures can't be re-explained by another key the hub could choose. */
function acceptableKey(c: PinnedCredential): boolean {
  if (c.alg === COSE_ALG.ES256 || c.alg === COSE_ALG.EdDSA || c.alg === COSE_ALG.Ed25519)
    return true
  if (c.alg !== COSE_ALG.RS256 || c.publicKey.kty !== 'RSA') return false
  // RSA: no tiny exponents or short moduli (no key chosen to fit a given signature).
  return c.publicKey.e === 'AQAB' && fromBase64Url(c.publicKey.n).length >= 256
}

/**
 * A signed-in browser with NO local pins (docs/security.md "Pin bootstrap from a locally verified
 * assertion"). `assertion` is the direct result of THIS page's navigator.credentials.get over
 * `challenge` (fresh, never sent anywhere yet). The hub supplies the credential's public key —
 * advisory only: it is pinned only if it verifies the authenticator's signature over
 * authenticatorData‖SHA-256(clientDataJSON), with clientData.type webauthn.get, our challenge, this
 * origin (not cross-origin), rpIdHash = SHA-256(this origin's host — the RP the browser enforced),
 * UP and UV set and a signCount that does not regress. The hub saw neither the challenge nor the
 * signature before it named the key, so it cannot pick a key that "fits". Its `revoked`/`held`
 * flags (and a 404 for a passkey not on the account) can only make this refuse.
 *
 * In "Just this session" (memory-only idb) the pin lives in memory and vanishes with the tab.
 */
export async function pinFromVerifiedAssertion(input: {
  accountId: string
  assertion: WebAuthnAssertion
  challenge: Uint8Array
  origin: string
  lookup: (credentialId: string) => Promise<UnlockCredential>
  now?: number
}): Promise<PinBootstrapResult> {
  const { accountId, assertion, challenge, origin } = input
  const fail = (reason: PinBootstrapFailure, detail?: string): PinBootstrapResult =>
    detail === undefined ? { ok: false, reason } : { ok: false, reason, detail }
  // A discoverable passkey names its account: one for another account is never pinned here.
  if (assertion.userHandle !== undefined) {
    let handle: string
    try {
      handle = fromUtf8(fromBase64Url(assertion.userHandle))
    } catch {
      return fail('wrong-account')
    }
    if (handle !== accountId) return fail('wrong-account')
  }
  let info: UnlockCredential
  try {
    info = await input.lookup(assertion.credentialId)
  } catch (e) {
    const status = (e as { status?: number }).status
    const code = (e as { code?: string }).code
    if (status === 404 || code === 'unlock_unknown_credential') return fail('not-on-account')
    return fail('credential-unavailable', code ?? (e instanceof Error ? e.message : String(e)))
  }
  if (info.credentialId !== assertion.credentialId) return fail('credential-mismatch')
  if (info.revoked) return fail('revoked-credential')
  if (info.held) return fail('recovery-hold')
  let pinned: PinnedCredential
  try {
    pinned = pinnedCredentialFromCose(info.credentialId, info.cose, {
      addedAt: input.now ?? Date.now(),
    })
  } catch {
    return fail('unsupported-key')
  }
  if (!acceptableKey(pinned)) return fail('unsupported-key')
  let rpId: string
  try {
    rpId = new URL(origin).hostname
  } catch {
    return fail('origin-mismatch')
  }
  const rp = { rpId, origins: [origin] }
  const verified = await verifyPasskeyApproval(assertion, challenge, {
    credentials: [pinned],
    rp,
    ...(info.counter > 0 ? { previousSignCount: () => info.counter } : {}),
  })
  if (!verified.ok) return fail(verified.reason, verified.detail)

  // Persist (CAS): never replace a trust record another tab wrote meanwhile, only add this pin.
  const fresh = new TrustStore({ rp, credentials: [pinned] })
  await fresh.verifyPasskeyApproval(assertion, challenge, { trackSignCount: true })
  await idb.update<TrustStoreSnapshot>(key(accountId), (current) => {
    if (!current) return fresh.toJSON()
    if (current.rp.rpId !== rpId || !current.rp.origins.includes(origin))
      throw new Error('trust origin mismatch')
    if (current.revokedCredentials.includes(pinned.credentialId))
      throw new Error('credential was revoked')
    if (current.credentials.some((c) => c.credentialId === pinned.credentialId)) return current
    return { ...current, credentials: [...current.credentials, pinned] }
  })
  await idb.update<Record<string, { source: PinProvenance; at: number }>>(
    provenanceKey(accountId),
    (current) => ({
      ...current,
      [pinned.credentialId]: current?.[pinned.credentialId] ?? {
        source: 'assertion',
        at: input.now ?? Date.now(),
      },
    }),
  )
  return { ok: true, trust: await loadContentTrust(accountId), credential: pinned }
}
